import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getWorkOrders, getAssets, getInvoices, getServiceRequests, getUsers,
  approveWorkOrder,
} from '../../services/api';
import {
  ClipboardList, Boxes, Users as UsersIcon, AlertTriangle, TrendingUp, DollarSign,
  ShieldCheck, Clock, CheckCircle2,
} from 'lucide-react';
import { StatCard, PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { WorkOrderDetailDialog } from '../../components/WorkOrderDetail';
import { MonthlyMarginChart } from '../../components/MonthlyMarginChart';
import { SitePerformance } from '../../components/SitePerformance';
import SiteMap from '../../components/SiteMap';

// ───────────────────── Revenue period helpers ─────────────────────
// Sum of invoice `amount` for issue dates within the given [start, end]
// window (inclusive). Voided / deleted invoices are ignored — they were
// cancelled so they don't count as booked revenue. Uses `issuedAt` (ISO
// string) with a fallback to `createdAt` if a row hasn't been formally
// issued yet.
const PERIOD_OPTIONS = [
  { value: 'mtd', label: 'Month to date' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'tax', label: 'This tax year' },
];

const periodRange = (key, now = new Date()) => {
  const y = now.getFullYear();
  const end = now;
  if (key === 'mtd') {
    return { start: new Date(y, now.getMonth(), 1), end };
  }
  if (key === 'ytd') {
    return { start: new Date(y, 0, 1), end };
  }
  // UK tax year — Aug 1 → Jul 31. If we're already past Aug 1 this calendar
  // year the window started this year; otherwise it started last Aug.
  const aug1ThisYear = new Date(y, 7, 1);
  const start = now >= aug1ThisYear ? aug1ThisYear : new Date(y - 1, 7, 1);
  return { start, end };
};

const sumRevenue = (invoices, start, end) => invoices.reduce((acc, inv) => {
  const xs = (inv.xeroStatus || '').toString().toUpperCase();
  if (xs === 'VOIDED' || xs === 'DELETED') return acc;
  const raw = inv.issuedAt || inv.createdAt;
  if (!raw) return acc;
  const t = new Date(raw);
  if (isNaN(t.getTime())) return acc;
  if (t < start || t > end) return acc;
  return acc + Number(inv.amount || 0);
}, 0);

const AdminOverview = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ wo: [], assets: [], invoices: [], requests: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [revPeriod, setRevPeriod] = useState('mtd');

  const refresh = useCallback(() => {
    return Promise.all([getWorkOrders(), getAssets(), getInvoices(), getServiceRequests(), getUsers()])
      .then(([wo, assets, invoices, requests, users]) => setData({ wo, assets, invoices, requests, users }));
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Revenue is recomputed whenever the period selector or the invoices
  // list changes. Cheap — just a filter + sum.
  const revenueForPeriod = useMemo(() => {
    const { start, end } = periodRange(revPeriod);
    return sumRevenue(data.invoices || [], start, end);
  }, [data.invoices, revPeriod]);

  // ─────────── "This Month" operational metrics ───────────
  // Only admin-created WOs feed the SLA trackers — engineer-raised jobs
  // are treated as "100% within time limits" by definition (no targets
  // to miss). An SLA denominator only counts WOs that actually have a
  // target set (blank target → skipped) so admins aren't penalised for
  // skipping the optional fields.
  const monthMetrics = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const completedThisMonth = (data.wo || []).filter((w) => {
      if (w.status !== 'completed') return false;
      const raw = w.completedAt || w.createdAt;
      if (!raw) return false;
      const t = new Date(raw);
      return !isNaN(t.getTime()) && t >= monthStart;
    });
    const adminCompleted = completedThisMonth.filter(
      (w) => (w.createdByRole || 'admin') === 'admin',
    );

    const withFinish = adminCompleted.filter((w) => w.finishByDate);
    const onTimeCount = withFinish.filter((w) => {
      const done = w.completedAt ? new Date(w.completedAt) : null;
      const due = new Date(w.finishByDate);
      return done && !isNaN(done.getTime()) && !isNaN(due.getTime()) && done <= due;
    }).length;

    const withHours = adminCompleted.filter((w) => Number(w.totalHoursEstimate) > 0);
    const onBudgetCount = withHours.filter((w) => {
      const actual = (w.labor || []).reduce((s, l) => s + Number(l.hours || 0), 0);
      return actual <= Number(w.totalHoursEstimate);
    }).length;

    const hoursAll = adminCompleted
      .map((w) => (w.labor || []).reduce((s, l) => s + Number(l.hours || 0), 0))
      .filter((h) => h > 0);
    const avgActualHours = hoursAll.length
      ? hoursAll.reduce((a, b) => a + b, 0) / hoursAll.length
      : 0;

    return {
      completedCount: completedThisMonth.length,
      adminCompletedCount: adminCompleted.length,
      onTimePct: withFinish.length ? Math.round((onTimeCount / withFinish.length) * 100) : null,
      onTimeBasis: withFinish.length,
      onBudgetPct: withHours.length ? Math.round((onBudgetCount / withHours.length) * 100) : null,
      onBudgetBasis: withHours.length,
      avgActualHours,
    };
  }, [data.wo]);

  if (loading) return <div className="p-8 text-slate-500">Loading...</div>;

  const open = data.wo.filter(w => w.status === 'open' || w.status === 'in_progress').length;
  const overdue = data.wo.filter(w => w.status === 'overdue').length;
  const admins = data.users.filter(u => u.role === 'admin').length;
  const employees = data.users.filter(u => u.role === 'employee').length;
  const pending = data.invoices.filter(i => i.status === 'pending').reduce((s, i) => s + (i.amount || 0), 0);
  const recent = data.wo.slice(0, 5);
  const recentRequests = data.requests.slice(0, 4);
  // Pending sign-off bucket — engineer-completed WOs the admin hasn't
  // reviewed yet. Sort newest-first since admins triage from the top.
  const pendingReview = data.wo.filter(w => w.needsAdminReview);
  const pendingReviewValue = pendingReview.reduce((s, w) =>
    s + (w.labor || []).reduce((a, l) => a + (Number(l.lineTotal) || 0), 0)
  + (w.partsUsed || []).filter(p => !p.nonBillable).reduce((a, p) => a + (Number(p.unitCost || 0) * Number(p.qty || 0)), 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader title={`Welcome back, ${user.name.split(' ')[0]}`} description="Here's what's happening across your operation today." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          icon={ClipboardList}
          label="Active Work Orders"
          value={open}
          hint={`${overdue} overdue`}
          accent={overdue > 0 ? 'red' : 'blue'}
          to="/admin/work-orders"
        />
        <PendingReviewStat
          count={pendingReview.length}
          value={pendingReviewValue}
        />
        <StatCard
          icon={Boxes}
          label="Total Assets"
          value={data.assets.length}
          hint={`${data.assets.filter(a => a.status === 'maintenance_due').length} need attention`}
          accent="amber"
          to="/admin/assets"
        />
        <StatCard
          icon={UsersIcon}
          label="Team"
          value={admins + employees}
          hint={`${admins} admin${admins === 1 ? '' : 's'} · ${employees} engineer${employees === 1 ? '' : 's'}`}
          accent="slate"
          to="/admin/users"
        />
        <RevenueStat
          value={revenueForPeriod}
          period={revPeriod}
          onPeriodChange={setRevPeriod}
          pending={pending}
        />
      </div>

      {pendingReview.length > 0 && (
        <PendingReviewPanel items={pendingReview} onChange={refresh} />
      )}

      <MonthlyMarginChart />

      <SitePerformance />

      {/* Site map — every customer site with lat/lng plotted with a
          coloured pin. Uses the same chartColor palette so this map
          and the margin chart share a visual language. */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display uppercase text-slate-900 tracking-wide">Site Map</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Every customer site plotted by postcode. Pin colour matches the customer&rsquo;s chart colour.
            </p>
          </div>
        </div>
        <div className="p-3">
          <SiteMap height={380} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <h2 className="font-display uppercase text-slate-900 tracking-wide">Recent Work Orders</h2>
            <Link to="/admin/work-orders" className="text-xs font-medium text-blue-700 hover:text-blue-800">View all →</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.map(w => (
              <div key={w.id} className="p-5 hover:bg-slate-50 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-slate-500">{w.id}</span>
                    <PriorityBadge priority={w.priority} />
                  </div>
                  <div className="font-medium text-slate-900 truncate">{w.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{w.assignee} · {w.customer}</div>
                </div>
                <StatusBadge status={w.status} />
              </div>
            ))}
            {recent.length === 0 && <div className="p-8 text-center text-slate-500">No work orders yet.</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <h2 className="font-display uppercase text-slate-900 tracking-wide">Work Requests</h2>
            <Link to="/admin/requests" className="text-xs font-medium text-blue-700 hover:text-blue-800">View all →</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentRequests.map(r => (
              <div key={r.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-slate-500">{r.id}</span>
                  <PriorityBadge priority={r.priority} />
                </div>
                <div className="text-sm font-medium text-slate-900">{r.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.customer}</div>
              </div>
            ))}
            {recentRequests.length === 0 && <div className="p-8 text-center text-slate-500">No requests.</div>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-blue-800" />
            <h2 className="font-display uppercase text-slate-900 tracking-wide">Attention Needed</h2>
          </div>
          <ul className="space-y-3">
            {data.wo.filter(w => w.status === 'overdue').map(w => (
              <li key={w.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded">
                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{w.title}</div>
                  <div className="text-xs text-slate-600">Due {w.dueDate} · {w.assignee}</div>
                </div>
              </li>
            ))}
            {data.assets.filter(a => a.status === 'maintenance_due').map(a => (
              <li key={a.id} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded">
                <AlertTriangle className="h-4 w-4 text-blue-800 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-600">Maintenance due · {a.location}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-slate-900 text-white rounded-md p-6 relative overflow-hidden" data-testid="month-metrics-card">
          <TrendingUp className="absolute -right-4 -bottom-4 h-32 w-32 text-blue-700/20" />
          <h2 className="font-display uppercase tracking-wide mb-2">This Month</h2>
          <p className="text-sm text-slate-300 mb-4">
            Tracked on admin-assigned jobs · engineer-raised jobs excluded.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <MonthStat
              label="Completed"
              value={monthMetrics.completedCount}
              sub={monthMetrics.adminCompletedCount !== monthMetrics.completedCount
                ? `${monthMetrics.adminCompletedCount} admin`
                : null}
              testId="metric-completed"
            />
            <MonthStat
              label="On-time rate"
              value={monthMetrics.onTimePct == null ? '—' : `${monthMetrics.onTimePct}%`}
              sub={monthMetrics.onTimeBasis
                ? `of ${monthMetrics.onTimeBasis} with target`
                : 'no finish-by set'}
              testId="metric-on-time"
            />
            <MonthStat
              label="On budget"
              value={monthMetrics.onBudgetPct == null ? '—' : `${monthMetrics.onBudgetPct}%`}
              sub={monthMetrics.onBudgetBasis
                ? `of ${monthMetrics.onBudgetBasis} with target`
                : 'no hours target'}
              testId="metric-on-budget"
            />
            <MonthStat
              label="Avg actual hours"
              value={monthMetrics.avgActualHours > 0
                ? `${monthMetrics.avgActualHours.toFixed(1)}h`
                : '—'}
              sub={monthMetrics.adminCompletedCount
                ? `across ${monthMetrics.adminCompletedCount} jobs`
                : null}
              testId="metric-avg-hours"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Stat tile with a built-in period selector for revenue.
 *  Separate from `StatCard` because the dropdown lives inside the tile
 *  and the value needs to wrap on large £ totals (£12,345,678 would blow
 *  out of a plain `text-3xl` tile on mobile). */
const RevenueStat = ({ value, period, onPeriodChange, pending }) => {
  const formatted = '£' + (value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-5 hover:shadow-sm transition-shadow flex flex-col min-w-0"
      data-testid="revenue-stat"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Revenue</div>
        <div className="h-8 w-8 rounded-md bg-green-100 text-green-700 flex items-center justify-center shrink-0">
          <DollarSign className="h-4 w-4" />
        </div>
      </div>
      {/* The value is clickable — opens the invoices list. We can't wrap
          the whole tile in a Link because the period dropdown lives inside
          and its pointer events would bubble to the link. */}
      <Link
        to="/admin/invoices"
        className="text-xl sm:text-2xl font-bold text-slate-900 tabular-nums leading-tight break-all hover:text-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
        data-testid="revenue-value"
      >
        {formatted}
      </Link>
      <div className="mt-2">
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger className="h-7 text-xs w-full" data-testid="revenue-period-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value} data-testid={`revenue-period-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {pending > 0 && (
        <div className="text-[11px] text-slate-500 mt-1.5">
          £{pending.toLocaleString('en-GB', { maximumFractionDigits: 0 })} pending
        </div>
      )}
    </div>
  );
};

/** Stat card variant for the "Pending sign-off" bucket. Distinct amber
 *  accent because it's an action-required tile, not a status snapshot.
 *  Clicking scrolls the page to the Pending review panel below (when
 *  anything is pending) — or does nothing when we're caught up. */
const PendingReviewStat = ({ count, value }) => {
  const tone = count > 0
    ? 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100'
    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50';
  const scrollTo = () => {
    const el = document.querySelector('[data-testid="pending-review-panel"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const Wrapper = count > 0 ? 'button' : 'div';
  const wrapperProps = count > 0
    ? { onClick: scrollTo, type: 'button', className: `rounded-md border p-4 text-left w-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 ${tone}`, 'data-testid': 'pending-review-stat' }
    : { className: `rounded-md border p-4 ${tone}`, 'data-testid': 'pending-review-stat' };
  return (
    <Wrapper {...wrapperProps}>
      <div className="flex items-center gap-2">
        <ShieldCheck className={`h-4 w-4 ${count > 0 ? 'text-amber-700' : 'text-slate-400'}`} />
        <span className="text-xs uppercase tracking-wider opacity-70">Pending sign-off</span>
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums">{count}</div>
      <div className="text-xs opacity-70 mt-1">
        {count > 0 ? <>£{value.toFixed(2)} ready to invoice</> : 'All caught up'}
      </div>
    </Wrapper>
  );
};

/** List of engineer-completed WOs awaiting admin sign-off. Each row gets
 *  a one-tap "Approve" CTA; rows where any labour entry needs a rate
 *  resolution are disabled (admin must resolve via the WO detail dialog
 *  first). Mobile-friendly — full-width tap targets, no nested controls. */
const PendingReviewPanel = ({ items, onChange }) => {
  const [busy, setBusy] = useState({}); // id → bool
  const [bulkBusy, setBulkBusy] = useState(false);
  const [detailWo, setDetailWo] = useState(null);

  const approveOne = async (wo, ev) => {
    if (ev) ev.stopPropagation();
    if (busy[wo.id]) return;
    if ((wo.labor || []).some(l => l.needsRateResolution)) {
      toast.error('Resolve pending labour rates first');
      setDetailWo(wo);
      return;
    }
    setBusy(s => ({ ...s, [wo.id]: true }));
    try {
      await approveWorkOrder(wo.id);
      toast.success(`${wo.id} approved · draft invoice queued`);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusy(s => ({ ...s, [wo.id]: false }));
    }
  };

  const approveAll = async () => {
    const ready = items.filter(w => !(w.labor || []).some(l => l.needsRateResolution));
    if (ready.length === 0) {
      toast.error('Nothing to approve — every pending WO needs a rate resolution');
      return;
    }
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    // Sequential — keeps backend Xero-accumulator queue calm + clearer error
    // surfacing if one fails halfway.
    for (const w of ready) {
      try { await approveWorkOrder(w.id); ok++; } catch { fail++; }
    }
    setBulkBusy(false);
    if (ok) toast.success(`Approved ${ok} ${ok === 1 ? 'work order' : 'work orders'}`);
    if (fail) toast.error(`${fail} failed — open them individually to investigate`);
    await onChange();
  };

  const blockedCount = items.filter(w => (w.labor || []).some(l => l.needsRateResolution)).length;

  return (
    <section data-testid="pending-review-panel" className="bg-white border border-amber-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-amber-200 bg-amber-50">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0" />
          <div className="min-w-0">
            <h2 className="font-display uppercase text-amber-900 tracking-wide truncate">Pending Sign-off</h2>
            <div className="text-xs text-amber-800 mt-0.5">
              {items.length} work {items.length === 1 ? 'order' : 'orders'} awaiting your review
              {blockedCount > 0 && <> · <span className="font-semibold">{blockedCount} blocked on rate resolution</span></>}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={approveAll}
          disabled={bulkBusy || items.length === 0}
          className="bg-amber-700 hover:bg-amber-800 text-white shrink-0"
          data-testid="bulk-approve-btn"
        >
          {bulkBusy ? 'Approving…' : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve all ready</>}
        </Button>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map(wo => {
          const blocked = (wo.labor || []).some(l => l.needsRateResolution);
          const totalHours = (wo.labor || []).reduce((a, l) => a + (Number(l.hours) || 0), 0);
          const totalCost = (wo.labor || []).reduce((a, l) => a + (Number(l.lineTotal) || 0), 0)
            + (wo.partsUsed || []).filter(p => !p.nonBillable).reduce((a, p) => a + (Number(p.unitCost || 0) * Number(p.qty || 0)), 0);
          return (
            <li
              key={wo.id}
              data-testid={`pending-review-row-${wo.id}`}
              onClick={() => setDetailWo(wo)}
              className="p-4 sm:p-5 hover:bg-slate-50 active:bg-slate-100 cursor-pointer flex items-center gap-3 sm:gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-xs font-mono text-slate-500">{wo.id}</span>
                  <PriorityBadge priority={wo.priority} />
                  {blocked && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">
                      needs rate
                    </span>
                  )}
                </div>
                <div className="font-medium text-slate-900 truncate">{wo.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1.5 flex-wrap">
                  <Clock className="h-3 w-3" />
                  <span>{totalHours.toFixed(2)}h · £{totalCost.toFixed(2)}</span>
                  <span className="opacity-50">·</span>
                  <span>{wo.assignee}</span>
                  {wo.customer && <><span className="opacity-50">·</span><span className="truncate">{wo.customer}</span></>}
                </div>
              </div>
              <Button
                size="sm"
                onClick={(e) => approveOne(wo, e)}
                disabled={busy[wo.id] || blocked}
                className={blocked
                  ? 'bg-slate-200 text-slate-500'
                  : 'bg-amber-700 hover:bg-amber-800 text-white'}
                data-testid={`approve-row-${wo.id}`}
              >
                {busy[wo.id] ? '…' : (blocked ? 'Resolve first' : 'Approve')}
              </Button>
            </li>
          );
        })}
      </ul>
      <WorkOrderDetailDialog
        wo={detailWo}
        open={!!detailWo}
        onOpenChange={(v) => { if (!v) setDetailWo(null); }}
        onUpdate={onChange}
      />
    </section>
  );
};

const MonthStat = ({ label, value, sub, testId }) => (
  <div data-testid={testId}>
    <div className="text-3xl font-bold text-blue-400 tabular-nums leading-tight">{value}</div>
    <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export default AdminOverview;
